import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/ui/dialog';
import { Button } from '@/ui/button';
import { Settings, Sparkles, Check, ChevronRight, Pencil } from 'lucide-react';
import {
  ApiModelType,
  ModelType,
  ModelConfig,
  getModelConfigList,
} from '@/services/admin';
import { AdminModelConfigModal } from './admin-model-config-modal';
import { cn } from '@/lib/utils';

// 前端展示的模型类型（不含 analysis）
type DisplayModelType = Exclude<ApiModelType, 'analysis'>;

// 模型类型信息
const MODEL_TYPE_INFO: Record<
  DisplayModelType,
  {
    name: string;
    description: string;
    recommendation: string;
    modelType: ModelType;
  }
> = {
  chat: {
    name: 'Chat 模型',
    description: '用于职位信息生成、智能匹配、面试管理等业务场景',
    recommendation: 'Deepseek V3.1',
    modelType: 'Chat',
  },
  embedding: {
    name: 'Embedding 向量模型',
    description: '用于简历解析高效检索识别等场景',
    recommendation: 'bge-m3',
    modelType: 'Embedding',
  },
};

// 模型配置顺序
const MODEL_CONFIG_ORDER: DisplayModelType[] = ['chat', 'embedding'];

interface ModelSetupWizardModalProps {
  open: boolean;
  missingTypes: ApiModelType[];
  tenantId: string | null;
  onComplete: () => void;
}

export function ModelSetupWizardModal({
  open,
  missingTypes: _missingTypes, // 保留 prop 兼容性，但使用本地状态判断
  tenantId,
  onComplete,
}: ModelSetupWizardModalProps) {
  // 当前正在配置的模型类型
  const [configuringType, setConfiguringType] =
    useState<DisplayModelType | null>(null);
  // 编辑中的配置（用于修改已配置的模型）
  const [editingConfig, setEditingConfig] = useState<ModelConfig | null>(null);
  // 模型配置列表
  const [modelConfigs, setModelConfigs] = useState<ModelConfig[]>([]);
  // 加载状态
  const [loading, setLoading] = useState(false);

  // 加载模型配置列表
  const loadModelConfigs = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const response = await getModelConfigList(tenantId);
      setModelConfigs(response.configs || []);
    } catch (error) {
      console.error('加载模型配置失败:', error);
      setModelConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // 基于本地配置列表计算已配置和未配置的类型
  const { configuredTypes, localMissingTypes } = useMemo(() => {
    const configured: ApiModelType[] = [];
    const missing: ApiModelType[] = [];

    MODEL_CONFIG_ORDER.forEach((type) => {
      const config = modelConfigs.find((c) => c.type === type && c.is_active);
      if (config) {
        configured.push(type);
      } else {
        missing.push(type);
      }
    });

    return { configuredTypes: configured, localMissingTypes: missing };
  }, [modelConfigs]);

  // 计算进度（基于本地状态）
  const totalTypes = MODEL_CONFIG_ORDER.length;
  const completedCount = configuredTypes.length;

  // 获取指定类型的模型配置
  const getModelConfigByType = useCallback(
    (type: ApiModelType): ModelConfig | undefined => {
      return modelConfigs.find(
        (config) => config.type === type && config.is_active
      );
    },
    [modelConfigs]
  );

  // 弹窗打开时加载配置
  useEffect(() => {
    if (open && tenantId) {
      loadModelConfigs();
    }
  }, [open, tenantId, loadModelConfigs]);

  // 检查是否全部配置完成（基于本地状态）
  useEffect(() => {
    // 只有在加载完成且所有模型都配置好时才触发完成回调
    if (
      open &&
      !loading &&
      modelConfigs.length > 0 &&
      localMissingTypes.length === 0
    ) {
      onComplete();
    }
  }, [
    open,
    loading,
    modelConfigs.length,
    localMissingTypes.length,
    onComplete,
  ]);

  // 开始配置某个模型（新建）
  const handleStartConfig = useCallback((type: DisplayModelType) => {
    setConfiguringType(type);
    setEditingConfig(null);
  }, []);

  // 开始编辑某个模型
  const handleStartEdit = useCallback(
    (type: DisplayModelType, config: ModelConfig) => {
      setConfiguringType(type);
      setEditingConfig(config);
    },
    []
  );

  // 配置成功回调
  const handleConfigSuccess = useCallback(async () => {
    setConfiguringType(null);
    setEditingConfig(null);
    // 重新加载配置列表以显示最新数据
    await loadModelConfigs();
  }, [loadModelConfigs]);

  // 关闭配置弹窗
  const handleConfigClose = useCallback(() => {
    setConfiguringType(null);
    setEditingConfig(null);
  }, []);

  if (!tenantId) {
    return null;
  }

  return (
    <>
      <Dialog open={open && !configuringType} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-[560px]"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-blue-100">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <DialogTitle className="text-xl">
                欢迎使用 WhaleHire 管理后台
              </DialogTitle>
            </div>
            <DialogDescription className="text-base">
              在开始使用之前，请先完成 AI 模型配置。
            </DialogDescription>
          </DialogHeader>

          {/* 进度指示 */}
          <div className="flex items-center gap-2 py-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                style={{ width: `${(completedCount / totalTypes) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-500 whitespace-nowrap">
              {completedCount}/{totalTypes} 已配置
            </span>
          </div>

          {/* 模型配置列表 */}
          <div className="py-2 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                加载中...
              </div>
            ) : (
              MODEL_CONFIG_ORDER.map((type) => {
                const info = MODEL_TYPE_INFO[type];
                const config = getModelConfigByType(type);
                // 使用本地状态判断是否已配置
                const isConfigured = configuredTypes.includes(type);
                const isMissing = localMissingTypes.includes(type);

                return (
                  <div
                    key={type}
                    className={cn(
                      'p-4 rounded-lg border transition-all',
                      isConfigured
                        ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      {/* 左侧内容 */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {info.name}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#10B981]/10 text-[#10B981]">
                            必选
                          </span>
                          {isConfigured && (
                            <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                              <Check className="w-3 h-3" />
                              已配置
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {info.description}
                        </p>

                        {/* 已配置时显示模型详情 */}
                        {isConfigured && config && (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-2">
                            <span>
                              <span className="text-gray-400">提供商:</span>{' '}
                              {config.provider}
                            </span>
                            <span>
                              <span className="text-gray-400">模型:</span>{' '}
                              {config.model_name}
                            </span>
                            {config.base_url && (
                              <span className="truncate max-w-[200px]">
                                <span className="text-gray-400">API:</span>{' '}
                                {config.base_url}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 未配置时显示推荐 */}
                        {isMissing && (
                          <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                            <Sparkles className="w-3 h-3" />
                            推荐: {info.recommendation}
                          </div>
                        )}
                      </div>

                      {/* 右侧操作按钮 */}
                      <div className="flex items-center gap-2 ml-4">
                        {isConfigured && config ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartEdit(type, config)}
                            className="h-8 px-3"
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            修改
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleStartConfig(type)}
                            className="text-white"
                            style={{
                              background:
                                'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
                            }}
                          >
                            配置
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* 底部提示 */}
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-400 text-center">
              请依次完成所有模型配置后进入系统
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* 模型配置弹窗 */}
      {configuringType && (
        <AdminModelConfigModal
          open={true}
          onOpenChange={(open) => {
            if (!open) handleConfigClose();
          }}
          tenantId={tenantId}
          modelType={MODEL_TYPE_INFO[configuringType].modelType}
          editingConfig={editingConfig}
          onSuccess={handleConfigSuccess}
        />
      )}
    </>
  );
}
